/**
 * App Router
 *
 * Public-web routes only — marketing, legal, blog, press. The deprecated
 * repo's app-router carried the full authenticated app surface (auth,
 * onboarding, research, profile, calendars, etc.) plus React.lazy code
 * splits per page. The marketing surface is small enough that eager
 * imports are fine; lazy chunks add complexity without payoff at this
 * scale.
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ROUTES } from './route-paths';
import { trackPageView } from '@logging';

import { LandingPage } from '@pages/landing-page';
import { InfoPage } from '@pages/info-page';
import { ContactUsPage } from '@pages/contact-us-page';
import { LegalIndexPage } from '@pages/legal-index-page';
import { PrivacyPolicyPage } from '@pages/privacy-policy-page';
import { TermsOfServicePage } from '@pages/terms-of-service-page';
import { DataUsePage } from '@pages/data-use-page';
import { DeleteDataPage } from '@pages/delete-data-page';
import { DeleteMyDataPage } from '@pages/delete-my-data-page';
import { NotFoundPage } from '@pages/not-found-page';
import { AIBlogsHomePage } from '@pages/ai-blogs/ai-blogs-home-page';
import { AIUnevenImpactBlogPage } from '@pages/ai-blogs/ai-uneven-impact-blog-page';
import { PressIndexPage } from '@pages/press/press-index-page';
import { BlueprintApril2026Page } from '@pages/press/blueprint-april-2026-page';
import { PreseedOct2026Page } from '@pages/press/preseed-oct-2026-page';
import { DTemplatePage } from '@pages/press/dtemplate-page';
import { ProductPage } from '@pages/product-page';

const PageViewTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

export const AppRouter: React.FC = () => (
  <BrowserRouter>
    <PageViewTracker />
    <Routes>
      <Route path={ROUTES.HOME} element={<LandingPage />} />
      <Route path={ROUTES.INFO} element={<InfoPage />} />
      <Route path={ROUTES.CONTACT_US} element={<ContactUsPage />} />
      <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
      <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
      <Route path={ROUTES.DATA_USE} element={<DataUsePage />} />
      <Route path={ROUTES.DELETE_DATA} element={<DeleteDataPage />} />
      <Route path={ROUTES.DELETE_MY_DATA} element={<DeleteMyDataPage />} />
      <Route path={ROUTES.AI_BLOGS} element={<AIBlogsHomePage />} />
      <Route path={ROUTES.AI_BLOGS_POST} element={<AIUnevenImpactBlogPage />} />
      <Route path={ROUTES.PRESS} element={<PressIndexPage />} />
      <Route path={ROUTES.PRESS_BLUEPRINT_APRIL_2026} element={<BlueprintApril2026Page />} />
      <Route path={ROUTES.PRESS_PRESEED_OCT_2026} element={<PreseedOct2026Page />} />
      <Route path={ROUTES.PRESS_DTEMPLATE} element={<DTemplatePage />} />
      <Route path={ROUTES.PRODUCT} element={<ProductPage />} />

      <Route path={ROUTES.LEGAL_INDEX} element={<LegalIndexPage />} />
      <Route path={ROUTES.LEGAL_PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
      <Route path={ROUTES.LEGAL_TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
      <Route path={ROUTES.LEGAL_DATA_USE} element={<DataUsePage />} />
      <Route path={ROUTES.LEGAL_CONTACT_US} element={<ContactUsPage />} />
      <Route path={ROUTES.LEGAL_DELETE_DATA} element={<DeleteDataPage />} />
      <Route path={ROUTES.LEGAL_DELETE_MY_DATA} element={<DeleteMyDataPage />} />

      <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
    </Routes>
  </BrowserRouter>
);
